/* eslint no-param-reassign: 0 */
import _ from "lodash";
import { currencyByCode } from "@/shared/utils/currencies";

const initialState = {
  token: null,
  user: null,
  loginReturnUrl: null,
  activeCompany: null
};

const gettersDef = {
  token: state => state.token,
  user: state => state.user,
  isLoggedIn: state => !!state.token,
  loginReturnUrl: state => state.loginReturnUrl,
  userCompanies: state => {
    if (!state.user) {
      return [];
    }
    const { userCompanies } = state.user;
    return userCompanies.map(uc => uc.company);
  },
  allowedApps: (state, getters) => {
    if (!state.user) {
      return [];
    }
    if (getters.isSuperUser || getters.isActiveCompanyAdmin) {
      return "*";
    }

    const { appLicenseGroupUsers } = state.user;
    return appLicenseGroupUsers.map(
      appLicenseGroupUser => appLicenseGroupUser.appLicenseGroup.appLicense.app
    );
  },
  activeCompany: (state, getters) => {
    if (state.activeCompany) {
      return state.activeCompany;
    }
    return getters.userCompanies.length
      ? getters.userCompanies[0].company
      : null;
  },
  activeCompanyCurrencySymbol: (state, getters) => {
    return currencyByCode(getters.activeCompany.defaultCurrencyCode)
      .symbol_native;
  },
  isActiveCompanyAdmin: (state, getters) => {
    if (!state.user) {
      return false;
    }
    if (!getters.activeCompany) {
      return false;
    }
    return state.user.userCompanies.find(
      uc => uc.company.id === getters.activeCompany.id && uc.isCompanyAdmin
    );
  },
  isSuperUser: (state, getters) => _.get(getters.user, "isSuperuser", false),
  assignedGroups: (state, getters) =>
    _.get(getters.user, "appLicenseGroupUsers", []).map(
      lgu => lgu.appLicenseGroup.group
    ),
  permissions: (state, getters) => {
    if (getters.isSuperUser || getters.isActiveCompanyAdmin) {
      return "*";
    }
    const r = _.mapValues(
      _.groupBy(
        _.flatMap(getters.assignedGroups, "groupPermissions"),
        "permission.codename"
      ),
      permission => permission
    );
    return r;
  }
};

const actions = {
  login({ dispatch, commit }, credentials) {
    return dispatch(
      "api/sendRequest",
      {
        method: "post",
        url: "/auth/login/",
        data: credentials,
        useAuth: false
      },
      { root: true }
    ).then(data => {
      commit("setData", data);
      if (!data.user.isSuperuser) {
        commit("setActiveCompany", {
          company: data.user.userCompanies[0].company
        });
      }
      return data;
    });
  },
  logout({ dispatch, commit }) {
    return dispatch(
      "api/sendRequest",
      { method: "post", url: "/auth/logout/" },
      { root: true }
    ).then(() => {
      commit("logout");
    });
  },
  getProfile({ dispatch, commit }) {
    return dispatch(
      "api/sendRequest",
      { method: "get", url: "/auth/user/" },
      { root: true }
    ).then(data => {
      commit("setUser", { user: data });
      if (!data.isSuperuser) {
        commit("setActiveCompany", { company: data.userCompanies[0].company });
      }
      return data;
    });
  },
  viewAsCompany({ commit, dispatch }, { companyId }) {
    return dispatch(
      "companies/fetchCompany",
      { id: companyId },
      { root: true }
    ).then(data => {
      commit("setActiveCompany", { company: data });
      return data;
    });
  },
  changeCurrentUserPassword({ dispatch }, { data }) {
    return dispatch(
      "api/sendRequest",
      { method: "post", url: "/auth/password/change/", data },
      { root: true }
    );
  },
  resetPassword({ dispatch }, { data }) {
    return dispatch(
      "api/sendRequest",
      { method: "post", url: "/auth/password/reset/", data },
      { root: true }
    );
  },
  resetPasswordConfirm({ dispatch }, { data }) {
    return dispatch(
      "api/sendRequest",
      { method: "post", url: "/auth/password/reset/confirm/", data },
      { root: true }
    );
  }
};

const mutations = {
  setData(state, { token, user }) {
    state.token = token;
    state.user = user;
  },
  setUser(state, { user }) {
    state.user = user;
  },
  logout(state) {
    state.token = null;
    state.user = null;
    state.loginReturnUrl = null;
    state.activeCompany = null;
  },
  setLoginReturnUrl(state, { url }) {
    state.loginReturnUrl = url;
  },
  setActiveCompany(state, { company }) {
    state.activeCompany = company;
  }
};

export default {
  namespaced: true,
  state: initialState,
  getters: gettersDef,
  actions,
  mutations
};
